/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * MyCertificatesPage — /feed/certificates
 * ---------------------------------------
 * Every certificate the signed-in person holds (their own and the family
 * members' on passes they booked), one section per event, newest first, with
 * an event filter and a search. Each card shows the live certificate and
 * offers Download PDF / PNG and Share (PNG or PDF).
 *
 * The email's "Download your certificate" opens /certificate/<id>
 * (CertificateLinkPage below, behind sign-in), which lands here with ?c=<id>:
 * that certificate is highlighted, scrolled to, and a banner offers its PDF.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon, Skeleton, LoadError } from '../../../components/feed/FeedParts';
import { groupByEvent, matchesSearch } from '../../../components/athlohive/myCertificates';
import { saveCertificateFile } from '../../../components/athlohive/certificateExport';
import useMyCertificates from './useMyCertificates';
import CertificateCard, { CertificatePicture, certificateFile } from './CertificateCard';
import { EVENTS_PATH, FEED_CERTIFICATES_PATH, FEED_PROFILE_PATH } from '../feedRoutes';
import useShellNav from '../useShellNav';

function useNotice() {
  const [notice, setNotice] = useState('');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const show = (text) => {
    clearTimeout(timer.current);
    setNotice(text);
    timer.current = setTimeout(() => setNotice(''), 2800);
  };
  return [notice, show];
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function EmptyState() {
  const embedded = useShellNav();
  return (
    <div className="fd-empty fd-cert-empty">
      <span className="fd-tico big"><Icon name="workspace_premium" /></span>
      <h3>No certificates yet</h3>
      <p>Join an event and check in on the day. When the organizer issues your certificate, it shows up here and you get an email with the download link.</p>
      <Link to={embedded ? '/feed/events' : EVENTS_PATH} className="fd-btn fd-btn-primary"><Icon name="calendar_month" />Find an event</Link>
    </div>
  );
}

function EmailBanner({ card, onDismiss, onNotice }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      saveCertificateFile(await certificateFile(card, 'pdf'));
      onNotice(`certificate-${card.certificateNo}.pdf downloaded`);
    } catch (_) {
      onNotice('The certificate could not be downloaded. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fd-cert-banner" role="status">
      <span className="fd-tico"><Icon name="mark_email_read" /></span>
      <div>
        <b>Opened from your email</b>
        <span>Your <b>{card.activity.name}</b> certificate{card.event.title ? ` from ${card.event.title}` : ''} is highlighted below. Download it as a PDF or an image.</span>
      </div>
      <button type="button" className="fd-btn fd-btn-primary" onClick={download} disabled={busy || !card.templateUrl}>
        <Icon name={busy ? 'hourglass_top' : 'download'} />{busy ? 'Preparing PDF…' : 'Download PDF'}
      </button>
      <button type="button" className="fd-iconbtn" aria-label="Dismiss" onClick={onDismiss}><Icon name="close" /></button>
    </div>
  );
}

export default function MyCertificatesPage() {
  const query = useMyCertificates();
  const [params, setParams] = useSearchParams();
  const linked = params.get('c') || '';
  const [eventId, setEventId] = useState('all');
  const [search, setSearch] = useState('');
  const [notice, showNotice] = useNotice();
  const cards = useMemo(() => query.data || [], [query.data]);
  const groups = useMemo(() => groupByEvent(cards), [cards]);
  const linkedCard = linked ? cards.find((c) => c.id === linked) : null;

  // Scroll the certificate from the email into view once it is on the page.
  useEffect(() => {
    if (!linkedCard) return;
    const el = document.getElementById(`certificate-${linkedCard.id}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [linkedCard]);

  const dismissLink = () => {
    const next = new URLSearchParams(params);
    next.delete('c');
    setParams(next, { replace: true });
  };

  const shown = groups
    .filter((g) => eventId === 'all' || g.id === eventId)
    .map((g) => ({ ...g, certificates: g.certificates.filter((c) => matchesSearch(c, search)) }))
    .filter((g) => g.certificates.length);

  const tabs = [{ id: 'all', label: 'All events', count: cards.length }]
    .concat(groups.map((g) => ({ id: g.id, label: g.title, count: g.certificates.length })));

  return (
    <main className="fd-sub fd-certs">
      <Link to={FEED_PROFILE_PATH} className="fd-cert-backlink"><Icon name="chevron_left" />Profile</Link>
      <div className="fd-fhead">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
          {cards.length > 0 && (
            <span className="fd-eyebrow"><Icon name="verified" fill />{plural(cards.length, 'certificate', 'certificates')} · {plural(groups.length, 'event', 'events')}</span>
          )}
          <h1>My certificates</h1>
        </div>
        {cards.length > 0 && (
          <label className="fd-search">
            <Icon name="search" />
            <input type="search" id="certificate-search" aria-label="Search certificates" placeholder="Search event, activity or name"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </label>
        )}
      </div>

      {query.isLoading && (
        <div className="fd-cert-grid" aria-busy="true" aria-label="Loading your certificates" style={{ marginTop: 28 }}>
          <Skeleton height={520} radius={24} /><Skeleton height={520} radius={24} />
        </div>
      )}
      {query.isError && <LoadError what="your certificates" onRetry={() => query.refetch()} />}

      {query.isSuccess && linked && !linkedCard && (
        <div className="fd-cert-banner is-warn" role="alert">
          <span className="fd-tico"><Icon name="info" /></span>
          <div><b>This certificate isn’t available on your account.</b><span>Sign in with the account the pass was booked on to see it.</span></div>
          <button type="button" className="fd-iconbtn" aria-label="Dismiss" onClick={dismissLink}><Icon name="close" /></button>
        </div>
      )}
      {linkedCard && <EmailBanner card={linkedCard} onDismiss={dismissLink} onNotice={showNotice} />}

      {query.isSuccess && cards.length === 0 && <EmptyState />}

      {cards.length > 0 && (
        <>
          <div className="fd-seg fd-cert-tabs" role="tablist" aria-label="Events">
            {tabs.map((t) => (
              <button key={t.id} type="button" role="tab" aria-selected={eventId === t.id} onClick={() => setEventId(t.id)}>
                <span className="fd-cert-tab-l">{t.label}</span><span className="fd-cnt">{t.count}</span>
              </button>
            ))}
          </div>
          <div className="fd-chips fd-cert-chips" role="group" aria-label="Events">
            {tabs.map((t) => (
              <button key={t.id} type="button" className="fd-chip" aria-pressed={eventId === t.id} onClick={() => setEventId(t.id)}>
                {t.label}<span className="c">{t.count}</span>
              </button>
            ))}
          </div>

          {shown.length === 0 && (
            <div className="fd-empty">
              <h3>No certificates match “{search}”</h3>
              <p>Try another event, activity or name.</p>
              <button type="button" className="fd-btn fd-btn-line" onClick={() => { setSearch(''); setEventId('all'); }}>Clear search</button>
            </div>
          )}

          {shown.map((g) => (
            <section key={g.id} aria-label={g.title}>
              <div className="fd-wsep fd-cert-sep">
                <b>{g.title}</b>
                <span>{[g.date, g.city].filter(Boolean).join(' · ')}</span>
                <span className="n">{plural(g.certificates.length, 'certificate', 'certificates')}</span>
              </div>
              <div className="fd-cert-grid">
                {g.certificates.map((card) => (
                  <CertificateCard key={card.id} card={card} highlighted={card.id === linked} onNotice={showNotice} />
                ))}
              </div>
            </section>
          ))}

          <p className="fd-cert-foot"><Icon name="info" />Certificates appear here once the event organizer issues them to participants who checked in. You also get an email with the download link. Certificates for family members on your passes are listed here too.</p>
        </>
      )}

      {notice && <div className="fd-cert-toast" role="status"><Icon name="check" />{notice}</div>}
    </main>
  );
}

/** /certificate/:certificateId, the email's download link: opens My certificates on that certificate. */
export function CertificateLinkPage() {
  const { certificateId } = useParams();
  return <Navigate to={`${FEED_CERTIFICATES_PATH}?c=${encodeURIComponent(certificateId || '')}`} replace />;
}

/** The "My certificates" card on Profile: the count and the two latest certificates. */
export function ProfileCertificatesCard() {
  const query = useMyCertificates();
  const cards = query.data || [];
  const latest = groupByEvent(cards).flatMap((g) => g.certificates).slice(0, 2);
  const sub = query.isLoading ? 'Loading…'
    : cards.length ? `${plural(cards.length, 'certificate', 'certificates')} · latest ${latest[0].activity.name}, ${latest[0].issued}`
      : 'Your event certificates will appear here';
  return (
    <section className="ap-card ap-s12 fd-cert-pf">
      <Link to={FEED_CERTIFICATES_PATH} className="fd-cert-pf-h">
        <span className="fd-tico"><Icon name="workspace_premium" fill /></span>
        <span className="t"><b>My event certificates</b><small>{sub}</small></span>
        {cards.length > 0 && <span className="fd-cert-pf-n">{cards.length}</span>}
        <Icon name="chevron_right" />
      </Link>
      {latest.length > 0 && (
        <Link to={FEED_CERTIFICATES_PATH} className="fd-cert-pf-pics" tabIndex={-1} aria-hidden="true">
          {latest.map((card) => <CertificatePicture key={card.id} card={card} showNumber={false} />)}
        </Link>
      )}
    </section>
  );
}
